import {
    isValidElement,
    type ReactNode,
    Children
} from "react";
import type {
    LayoutRectangle
} from "react-native";
import type {
    TabsScrollConversionConstantType,
    TabsNavigationIndexConstantType,
    TabsOverflowTitleConstantType,
    TabsOrderedValuesConstantType,
    TabsOverflowModeConstantType,
    TabsDefaultValueConstantType,
    TabsHiddenValuesConstantType,
    TabsMenuLayoutConstantType,
    TabsSwapOffsetConstantType,
    TabsCountTextConstantType,
    TabsThumbRectConstantType,
    TabsSwapStepConstantType,
    TabsTabStopConstantType,
    TabsKeyStepConstantType,
    TabsRevealConstantType,
    TabsIDConstantType,
    TabsPartFlagProps,
    TabsScrollMetrics,
    TabsOverflowMode,
    TabsTriggerItem,
    TabsScrollEdges,
    TabsSwapStep,
    TabsPartKind,
    TabsRect
} from "./type";
import {
    getHorizontalStep,
    getDirectionSign
} from "../../helpers/direction";
import {
    formatNumber
} from "../../helpers/locale";

export const TABS_NAVIGATION_KEYS: Array<string> = [
    "ArrowRight",
    "ArrowLeft",
    "ArrowDown",
    "ArrowUp",
    "Home",
    "End"
];

export const TABS_PART_KINDS: Array<TabsPartKind> = [
    "content",
    "trigger",
    "list",
    "root"
];

export const TABS_SPACE_KEY = " ";

export const TABS_MAX_COUNT = 99;

export const TABS_SEGMENT_MAX_TRIGGERS = 5;

export const TABS_SCROLL_EPSILON = 1;

export const TABS_OVERFLOW_EPSILON = 1;

export const TABS_MEASURE_EPSILON = 0.1;

const isTabsPartKind = (value: unknown): value is TabsPartKind => {
    return typeof value === "string" && TABS_PART_KINDS.some((kind) => kind === value);
};

export const getTabsPartKind = (node: ReactNode): TabsPartKind | undefined => {
    if(!isValidElement(node)) {
        return undefined;
    }

    const elementType: unknown = node.type;

    if(typeof elementType !== "function" || !("tabsPart" in elementType)) {
        return undefined;
    }

    return isTabsPartKind(elementType.tabsPart) ? elementType.tabsPart : undefined;
};

export const getTabsTriggerItems = ({
    children
}: {
    children: ReactNode;
}): Array<TabsTriggerItem> => {
    const items: Array<TabsTriggerItem> = [];

    const visit = (node: ReactNode) => {
        Children.forEach(node, (child) => {
            if(!isValidElement<TabsPartFlagProps>(child)) {
                return;
            }

            const kind = getTabsPartKind(child);

            if(kind === "trigger") {
                if(typeof child.props.value === "string") {
                    items.push({
                        count: typeof child.props.count === "number" || typeof child.props.count === "string" ? child.props.count : undefined,
                        accessibilityLabel: child.props.accessibilityLabel ?? child.props["aria-label"],
                        title: typeof child.props.title === "string" ? child.props.title : undefined,
                        isDisabled: child.props.isDisabled === true,
                        value: child.props.value,
                        icon: child.props.icon
                    });
                }

                return;
            }

            if(kind === "content" || kind === "root") {
                return;
            }

            visit(child.props.children);
        });
    };

    visit(children);

    return items;
};

export const getTabsDefaultValue = ({
    items
}: TabsDefaultValueConstantType): string | undefined => {
    return items.find((item) => !item.isDisabled)?.value;
};

export const getTabsTabStopValue = ({
    isDisabled,
    items,
    value
}: TabsTabStopConstantType): string | undefined => {
    if(isDisabled) {
        return undefined;
    }

    if(items.some((item) => item.value === value && !item.isDisabled)) {
        return value;
    }

    return items.find((item) => !item.isDisabled)?.value;
};

export const getTabsOrderedValues = ({
    triggers,
    items
}: TabsOrderedValuesConstantType): Array<string> => {
    const values: Array<string> = [];

    items.forEach((item) => {
        const entry = triggers.get(item.value);

        if(entry && !entry.isDisabled && !entry.isHidden && !values.includes(item.value)) {
            values.push(item.value);
        }
    });

    triggers.forEach((entry, value) => {
        if(!entry.isDisabled && !entry.isHidden && !values.includes(value)) {
            values.push(value);
        }
    });

    return values;
};

export const getTabsSwapStep = ({
    previousValue,
    items,
    value
}: TabsSwapStepConstantType): TabsSwapStep => {
    if(previousValue === undefined || value === undefined || previousValue === value) {
        return 0;
    }

    const previousIndex = items.findIndex((item) => item.value === previousValue);

    const index = items.findIndex((item) => item.value === value);

    return index < previousIndex ? -1 : 1;
};

export const getTabsSwapOffset = ({
    orientation,
    direction,
    spaces,
    step
}: TabsSwapOffsetConstantType): number => {
    const sign = orientation === "vertical" ? 1 : getDirectionSign({
        direction
    });

    return step * sign * spaces.spacingSm;
};

export const getTabsKeyStep = ({
    orientation,
    direction,
    listRole,
    key
}: TabsKeyStepConstantType): number => {
    const horizontalStep = getHorizontalStep({
        direction,
        key
    });

    let verticalStep = 0;

    if(key === "ArrowDown") {
        verticalStep = 1;
    }

    if(key === "ArrowUp") {
        verticalStep = -1;
    }

    if(listRole === "radiogroup") {
        return verticalStep || horizontalStep;
    }

    return orientation === "vertical" ? verticalStep : horizontalStep;
};

export const getTabsNavigationIndex = ({
    current,
    count,
    step,
    key
}: TabsNavigationIndexConstantType): number => {
    if(!count) {
        return -1;
    }

    if(key === "Home") {
        return 0;
    }

    if(key === "End") {
        return count - 1;
    }

    if(!step) {
        return -1;
    }

    if(current < 0) {
        return step > 0 ? 0 : count - 1;
    }

    return (current + step + count) % count;
};

export const getTabsID = ({
    baseID,
    value,
    kind
}: TabsIDConstantType): string => {
    return `${baseID}-${kind}-${encodeURIComponent(value)}`;
};

export const getTabsCountText = ({
    locale,
    count
}: TabsCountTextConstantType): string => {
    if(typeof count === "string") {
        return count;
    }

    if(count > TABS_MAX_COUNT) {
        return `${formatNumber({
            value: TABS_MAX_COUNT,
            locale
        })}+`;
    }

    return formatNumber({
        value: Math.max(0, Math.floor(count)),
        locale
    });
};

export const getTabsThumbRect = ({
    measures,
    layout
}: TabsThumbRectConstantType): TabsRect | undefined => {
    if(!layout) {
        return undefined;
    }

    return {
        y: layout.y + (layout.height - measures.itemHeight) / 2,
        width: layout.width - measures.cellInset * 2,
        x: layout.x + measures.cellInset,
        height: measures.itemHeight
    };
};

export const getTabsOverflowMode = ({
    overflowBehaviour,
    isOverflowAware,
    pointerType
}: TabsOverflowModeConstantType): TabsOverflowMode | undefined => {
    if(!isOverflowAware) {
        return undefined;
    }

    if(overflowBehaviour === "auto") {
        return pointerType === "coarse" ? "scroll" : "menu";
    }

    return overflowBehaviour;
};

export const getTabsHiddenValues = ({
    availableWidth,
    pinnedValue,
    moreWidth,
    moreGap,
    widths,
    items,
    gap
}: TabsHiddenValuesConstantType): Array<string> => {
    if(availableWidth <= 0 || items.length < 2) {
        return [];
    }

    const sizes: Array<number> = [];

    for(const item of items) {
        const width = widths[item.value];

        if(width === undefined) {
            return [];
        }

        sizes.push(width);
    }

    const totalWidth = sizes.reduce((total, size) => total + size, 0) + gap * (sizes.length - 1);

    if(totalWidth <= availableWidth + TABS_OVERFLOW_EPSILON) {
        return [];
    }

    const budget = availableWidth - moreWidth - moreGap;

    const pinnedIndex = items.findIndex((item) => item.value === pinnedValue);

    const pinnedSize = sizes[pinnedIndex] ?? 0;

    const visibleIndexes = new Set<number>();

    let usedWidth = 0;

    for(let index = 0; index < items.length; index++) {
        const nextWidth = usedWidth + (visibleIndexes.size ? gap : 0) + (sizes[index] ?? 0);

        const reservedWidth = pinnedIndex > index ? gap + pinnedSize : 0;

        if(index !== pinnedIndex && nextWidth + reservedWidth > budget + TABS_OVERFLOW_EPSILON) {
            break;
        }

        visibleIndexes.add(index);

        usedWidth = nextWidth;
    }

    if(pinnedIndex >= 0) {
        visibleIndexes.add(pinnedIndex);
    }

    if(!visibleIndexes.size) {
        visibleIndexes.add(0);
    }

    return items.filter((_, index) => !visibleIndexes.has(index)).map((item) => item.value);
};

export const getTabsMenuLayout = ({
    hiddenValues,
    cellHeight,
    cellInset,
    widths,
    items,
    value,
    gap
}: TabsMenuLayoutConstantType): LayoutRectangle | undefined => {
    let offset = 0;

    for(const item of items) {
        if(hiddenValues.includes(item.value)) {
            continue;
        }

        const width = widths[item.value];

        if(width === undefined) {
            return undefined;
        }

        if(item.value === value) {
            return {
                width: width + cellInset * 2,
                x: offset - cellInset,
                height: cellHeight,
                y: 0
            };
        }

        offset += width + gap;
    }

    return undefined;
};

export const getTabsOverflowTitle = ({
    locale,
    item
}: TabsOverflowTitleConstantType): string => {
    const title = item.title ?? item.accessibilityLabel ?? item.value;

    if(item.count === undefined) {
        return title;
    }

    return `${title} · ${getTabsCountText({
        count: item.count,
        locale
    })}`;
};

const getTabsMaxOffset = (metrics: TabsScrollMetrics): number => {
    return Math.max(0, metrics.contentWidth - metrics.viewportWidth);
};

export const getTabsPhysicalOffset = ({
    direction,
    metrics,
    isWeb
}: TabsScrollConversionConstantType): number => {
    if(direction === "rtl" && isWeb) {
        return metrics.offset + getTabsMaxOffset(metrics);
    }

    return metrics.offset;
};

export const getTabsScrollOffset = ({
    direction,
    metrics,
    isWeb
}: TabsScrollConversionConstantType): number => {
    if(direction === "rtl" && isWeb) {
        return metrics.offset - getTabsMaxOffset(metrics);
    }

    return metrics.offset;
};

export const getTabsScrollEdges = ({
    direction,
    metrics,
    isWeb
}: TabsScrollConversionConstantType): TabsScrollEdges => {
    if(!metrics.viewportWidth) {
        return {
            isFadeStart: false,
            isFadeEnd: false
        };
    }

    const left = getTabsPhysicalOffset({
        direction,
        metrics,
        isWeb
    });

    const right = getTabsMaxOffset(metrics) - left;

    const isFadeLeft = left > TABS_SCROLL_EPSILON;

    const isFadeRight = right > TABS_SCROLL_EPSILON;

    if(direction === "rtl") {
        return {
            isFadeStart: isFadeRight,
            isFadeEnd: isFadeLeft
        };
    }

    return {
        isFadeStart: isFadeLeft,
        isFadeEnd: isFadeRight
    };
};

export const getTabsRevealOffset = ({
    direction,
    fadeWidth,
    rowInset,
    metrics,
    layout,
    isWeb
}: TabsRevealConstantType): number | undefined => {
    const maxOffset = getTabsMaxOffset(metrics);

    if(maxOffset <= 0 || !metrics.viewportWidth) {
        return undefined;
    }

    const left = getTabsPhysicalOffset({
        direction,
        metrics,
        isWeb
    });

    const visibleStart = left + fadeWidth;

    const visibleEnd = left + metrics.viewportWidth - fadeWidth;

    const layoutStart = layout.x + rowInset;

    const layoutEnd = layoutStart + layout.width;

    let target = left;

    if(layoutStart < visibleStart) {
        target = layoutStart - fadeWidth;
    } else if(layoutEnd > visibleEnd) {
        target = layoutEnd - metrics.viewportWidth + fadeWidth;
    } else {
        return undefined;
    }

    target = Math.min(Math.max(target, 0), maxOffset);

    if(target === left) {
        return undefined;
    }

    return getTabsScrollOffset({
        metrics: {
            ...metrics,
            offset: target
        },
        direction,
        isWeb
    });
};
